// Custom form validation utility

// Centralized regex constants for standard string inputs (aligned with HRMS HolidayCalendar / forms)
export const INVALID_CHARS_REGEX = /[^a-zA-Z0-9\s.,\-_'"@+#/\\\[\]{}():;!?&%*=]/g;
export const VALID_TEXT_REGEX = /^[a-zA-Z0-9\s.,\-_'"@+#/\\\[\]{}():;!?&%*=]+$/;

export const validateField = (name, value, rules = {}) => {
  const errors = [];

  // Required field validation
  if (rules.required && (!value || value.trim() === '')) {
    const fieldLabel = rules.customLabel || getFieldLabel(name);
    errors.push(`${fieldLabel} is required.`);
    return errors;
  }

  // Skip other validations if field is empty and not required
  if (!value || value.trim() === '') {
    return errors;
  }

  // Email validation
  if (rules.email || (name.toLowerCase().includes('email') && !name.toLowerCase().includes('phone'))) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      errors.push('Please enter a valid email address.');
    }
    // Check maxLength for email (default 100 if not specified)
    const emailMaxLength = rules.maxLength || 100;
    if (value.length > emailMaxLength) {
      errors.push(`Email must not exceed ${emailMaxLength} characters.`);
    }
  }

  // Phone validation
  if ((rules.phone || (name.toLowerCase().includes('phone') && !name.toLowerCase().includes('email'))) && !rules.pattern) {
    // Remove all non-digit characters for validation
    const cleanPhone = value.replace(/\D/g, '');
    
    // Check for phone numbers (10 digits starting with 1-9)
    if (cleanPhone.length === 10) {
      if (!/^[1-9]/.test(cleanPhone)) {
        errors.push('Phone number must start with digits 1-9.');
      }
    } else if (cleanPhone.length === 12 && cleanPhone.startsWith('91')) {
      // Handle +91 prefix
      const phoneWithoutCountryCode = cleanPhone.substring(2);
      if (!/^[1-9]/.test(phoneWithoutCountryCode)) {
        errors.push('Phone number must start with digits 1-9.');
      }
    } else {
      errors.push('Please enter a valid 10-digit phone number.');
    }
  }

  // Password validation - check minimum and maximum length
  if (rules.password || name.toLowerCase().includes('password')) {
    if (value.length < 6) {
      errors.push('Password must be at least 6 characters long.');
    }
    if (value.length > 72) {
      errors.push('Password must not exceed 72 characters.');
    }
  }

  // Confirm password validation
  if (name.toLowerCase().includes('confirm') && name.toLowerCase().includes('password')) {
    // This will be handled in the form component with the original password
    return errors;
  }

  // Get field label (use customLabel if provided)
  const fieldLabel = rules.customLabel || getFieldLabel(name);

  // Min length validation
  if (rules.minLength && value.length < rules.minLength) {
    errors.push(`${fieldLabel} must be at least ${rules.minLength} characters long`);
  }

  // Max length validation
  if (rules.maxLength && value.length > rules.maxLength) {
    errors.push(`${fieldLabel} must not exceed ${rules.maxLength} characters`);
  }

  // Numeric validation
  if (rules.numeric && isNaN(value)) {
    errors.push(`${fieldLabel} must be a valid number`);
  }

  // Custom regex validation
  if (rules.pattern && !rules.pattern.test(value)) {
    errors.push(rules.patternMessage || `${fieldLabel} format is invalid`);
  }

  return errors;
};

// Get user-friendly field labels
const getFieldLabel = (name) => {
  const labels = {
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone Number',
    emailOrPhone: 'Email or Phone',
    name: 'Name',
    title: 'Title',
    description: 'Description',
    company: 'Company',
    address: 'Address',
    city: 'City',
    state: 'State',
    zipCode: 'ZIP Code',
    country: 'Country',
    department: 'Department',
    position: 'Position',
    employeeId: 'Employee ID',
    salary: 'Salary',
    startDate: 'Start Date',
    endDate: 'End Date',
    reason: 'Reason',
    message: 'Message',
    subject: 'Subject',
    otp: 'OTP',
    newPassword: 'New Password',
    currentPassword: 'Current Password'
  };

  // Convert camelCase to readable format
  if (labels[name]) {
    return labels[name];
  }

  // Fallback: convert camelCase to Title Case
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Validate entire form
export const validateForm = (formData, validationRules) => {
  const errors = {};
  let isValid = true;

  Object.keys(validationRules).forEach(fieldName => {
    // Trim the value before validation to handle leading/trailing spaces
    const trimmedValue = typeof formData[fieldName] === 'string' ? formData[fieldName].trim() : formData[fieldName];
    const fieldErrors = validateField(fieldName, trimmedValue, validationRules[fieldName]);
    if (fieldErrors.length > 0) {
      errors[fieldName] = fieldErrors[0]; // Take first error message
      isValid = false;
    }
  });

  // Special validation for confirm password
  if (formData.confirmPassword && (formData.password || formData.newPassword)) {
    const originalPassword = formData.password || formData.newPassword;
    if (formData.confirmPassword !== originalPassword) {
      errors.confirmPassword = 'Passwords do not match.';
      isValid = false;
    }
  }

  return { errors, isValid };
};

// Common validation rules
export const VALIDATION_RULES = {
  email: {
    required: true,
    email: true,
    maxLength: 100
  },
  password: {
    required: true,
    password: true,
    minLength: 6,
    maxLength: 72
  },
  confirmPassword: {
    required: true
  },
  phone: {
    required: true,
    phone: true
  },
  name: {
    required: true,
    minLength: 2
  },
  firstName: {
    required: true,
    minLength: 2
  },
  lastName: {
    required: true,
    minLength: 2
  },
  otp: {
    required: true,
    pattern: /^\d{6}$/,
    patternMessage: 'OTP must be 6 digits'
  },
  required: {
    required: true
  }
};

// Custom error message component
export const ErrorMessage = ({ error, className = "" }) => {
  if (!error) return null;
  
  return (
    <div className={`text-red-500 text-sm mt-1 ${className}`}>
      {error}
    </div>
  );
};

// Form field wrapper with error display
export const FormField = ({ 
  children, 
  error, 
  label, 
  required = false, 
  className = "" 
}) => {
  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {children}
      <ErrorMessage error={error} />
    </div>
  );
};
